import React, { useReducer } from "react";
import PropTypes from "prop-types";

import ProgramPerformanceTableHeaderColumn from "./TableHeaderColumn";
import ProgramPerformanceReports from "./ExtraInfo/Reports";
import ProgramPerformanceMoreInfoLink from "./MoreInfoLink";
import ProgramPerformanceCustomerSatisfaction from "./ExtraInfo/CustomerSatisfaction";
import ProgramPerformanceInformationDistribution from "./ExtraInfo/InformationDistribution";
import ProgramPerformanceContractAdherence from "./ExtraInfo/ContractAdherence";
import ProgramPerformanceDelivery from "./ExtraInfo/Delivery";

function extraInfoReducer(state, action) {
  if (action.type === state) {
    return null;
  } else {
    return action.type;
  }
}

/**
 * Contract Holder Program Performance Table Header Component
 * @param {Object} props
 * @param {"asc" | "desc"} props.sortDir
 * @param {number} props.sortCol
 * @param {function} props.contractHolderPerformancesDispatch
 * @returns {Element}
 * @constructor
 */
export default function TableHeader({ sortDir, sortCol, contractHolderPerformancesDispatch }) {
  const [extraInfo, extraInfoDispatch] = useReducer(extraInfoReducer, null);

  /**
   * Gets more info content if expanded, null if not expanded
   * @returns {React.JSX.Element|null}
   */
  function getExtraInfoContent() {
    switch (extraInfo) {
      case "reports":
        return <ProgramPerformanceReports/>;
      case "customer satisfaction":
        return <ProgramPerformanceCustomerSatisfaction/>;
      case "information distribution":
        return <ProgramPerformanceInformationDistribution/>;
      case "contract adherence":
        return <ProgramPerformanceContractAdherence/>;
      case "delivery":
        return <ProgramPerformanceDelivery/>;
      default:
        return null;
    }
  }

  /**
   * OnClick handler for more info link
   * @param {string} type
   * @param {Event} event
   */
  function handleOnClick(type, event) {
    event.stopPropagation();
    extraInfoDispatch({ type });
  }

  const extraInfoContent = getExtraInfoContent();
  const icon = sortDir === "asc" ? "glyphicon glyphicon-arrow-up" : "glyphicon glyphicon-arrow-down";
  const ariaLabel = sortDir === "asc" ? "sorted in A to Z order" : "sorted in Z to A order";

  return (<thead>
  <tr>
    <ProgramPerformanceTableHeaderColumn
      onClick={() => contractHolderPerformancesDispatch({ type: "sort", sortCol: 0 })}
      title="Sort by Contract Holders"
      className="ch-name-header clickable"
    >
      Contract Holders
      {sortCol === 0 && <span aria-label={ariaLabel}><i className={icon}></i></span>}
    </ProgramPerformanceTableHeaderColumn>
    <ProgramPerformanceTableHeaderColumn
      onClick={() => contractHolderPerformancesDispatch({ type: "sort", sortCol: 1 })}
      title="Sort by reports rating"
      className="clickable"
    >
      Reports
      {sortCol === 1 && <span aria-label={ariaLabel}><i className={icon}></i></span>}
      <ProgramPerformanceMoreInfoLink
        handleClick={handleOnClick} actionType="reports"
      />
    </ProgramPerformanceTableHeaderColumn>
    <ProgramPerformanceTableHeaderColumn
      onClick={() => contractHolderPerformancesDispatch({ type: "sort", sortCol: 2 })}
      title="Sort by customer satisfaction rating"
      className="clickable"
    >
      Customer Satisfaction
      {sortCol === 2 && <span aria-label={ariaLabel}><i className={icon}></i></span>}
      <ProgramPerformanceMoreInfoLink
        handleClick={handleOnClick} actionType="customer satisfaction"
      />
    </ProgramPerformanceTableHeaderColumn>
    <ProgramPerformanceTableHeaderColumn
      onClick={() => contractHolderPerformancesDispatch({ type: "sort", sortCol: 3 })}
      title="Sort by information distribution rating"
      className="clickable"
    >
      Information Distribution
      {sortCol === 3 && <span aria-label={ariaLabel}><i className={icon}></i></span>}
      <ProgramPerformanceMoreInfoLink
        handleClick={handleOnClick} actionType="information distribution"
      />
    </ProgramPerformanceTableHeaderColumn>
    <ProgramPerformanceTableHeaderColumn
      onClick={() => contractHolderPerformancesDispatch({ type: "sort", sortCol: 4 })}
      title="Sort by contract adherence rating"
      className="clickable"
    >
      Contract Adherence
      {sortCol === 4 && <span aria-label={ariaLabel}><i className={icon}></i></span>}
      <ProgramPerformanceMoreInfoLink
        handleClick={handleOnClick} actionType="contract adherence"
      />
    </ProgramPerformanceTableHeaderColumn>
    <ProgramPerformanceTableHeaderColumn
      onClick={() => contractHolderPerformancesDispatch({ type: "sort", sortCol: 5 })}
      title="Sort by delivery rating"
      className="clickable"
    >
      Delivery
      {sortCol === 5 && <span aria-label={ariaLabel}><i className={icon}></i></span>}
      <ProgramPerformanceMoreInfoLink
        handleClick={handleOnClick} actionType="delivery"
      />
    </ProgramPerformanceTableHeaderColumn>
  </tr>
  {extraInfoContent}
  </thead>);
}

TableHeader.propTypes = {
  sortDir: PropTypes.string, sortCol: PropTypes.number, contractHolderPerformancesDispatch: PropTypes.func,
};